import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Spin, Input, Pagination, Radio, Button as AntButton, Tooltip, message } from 'antd'
import { COLORS } from 'theme'
import { SearchOutlined, ArrowUpOutlined, ArrowDownOutlined, DownloadOutlined } from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import { exportScheduleToExcel } from '../../actions'
import Button from 'components/Button'
import CheckIconWhite from 'components/icons/CheckIconWhite'
import VirtualizedCheckboxGroup from 'components/UIComponents/Filter'
import { StateCityMessage } from '../../../SystemConfig/Partials/TableSettings/Style/style.js'
import FilterIcon from '../../../../components/icons/FilterIcon'
import ArrowDropDown from '../../../../components/icons/ArrowDropDown'
import AssessmentProgressBar from 'components/AssessmentProgressBar'
import CompletedAssessmentProgressBar from 'components/CompletedAssessmentProgressBar'
import RightSideArrowIco from 'components/icons/RightSideArrowIco'
import DownSideArrowIco from 'components/icons/DownSideArrowIco'
import ExpandableContent from './ExpandableContent'

import {
  MenuFlex,
  StyledTab,
  SubmitFlex,
} from 'modules/Dashboard/Partials/UsersFilter/Styles/style'
import FilterButtonComponent from 'components/FilterButton'
import { FilterContainer, FilterPopup } from 'components/FilterButton/styles'
import CollegeCard from 'components/CollegeCard'
import CheckboxGroup from 'components/CheckboxGroup'
import RadioButtonGroup from 'components/RadioButtonGroup'
import { Link } from 'react-router-dom'
import CustomDateCalendarDrawer from './CustomDateCalendarDrawer'
import moment from 'moment'

import {
  SearchSection,
  ShowingText,
  Overlay,
  TableTop,
  CustomStyledTable,
  AssessmentName,
} from '../../Style/style'

const formatDate = dateString => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  // Assessment start/end are stored as IST-wall-clock-as-UTC; render in UTC so the
  // date matches admin-react (else IST browsers show +1 day past 18:30 UTC).
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

const assessmentTypeOptions = [
  { value: 'communication', label: 'Communication Assessment' },
  { value: 'behavior', label: 'Behavioral Assessment' },
  { value: 'aptitude', label: 'Aptitude' },
  { value: 'role_based', label: 'Role Based Assessment' },
  { value: 'custom_assessment', label: 'Custom Assessment' },
  { value: 'technical_mcq', label: 'Technical MCQ' },
  { value: 'technical_coding', label: 'Technical Coding' },
  { value: '', label: 'All Assessment Types' },
]

const UnifiedAssessmentTable = ({
  activeAssessments = [],
  completedAssessments = [],
  loading = false,
  filters = {},
  activeMetrics,
  completedMetrics,
  activePage = 1,
  completedPage = 1,
  activeLimit = 10,
  completedLimit = 10,
  activeTotalItems = 0,
  completedTotalItems = 0,
  onFilterChange = () => { },
  onPageChange = () => { },
  stateCityPairs = [],
  onAssessmentClick = () => { },
  instituteId = null,
  passingYear = null,
}) => {
  const dispatch = useDispatch()
  const [trialPeriod, setTrialPeriod] = useState(filters.isTrial || false)
  const [showViewCalendar, setShowViewCalendar] = useState(false)
  const [viewCalendarData, setViewCalendarData] = useState(null)
  const [subscription, setSubscription] = useState(
    filters.isSubscribed || false
  )
  const [showFilters, setShowFilters] = useState(false)
  const [selectedAssessmentType, setSelectedAssessmentType] = useState(
    filters.type_name || ''
  )
  const [searchValue, setSearchValue] = useState(filters.searchBy || '')
  const [statusFilter, setStatusFilter] = useState('active') // active, completed

  const { states, citiesByState } = useMemo(() => {
    const statesSet = new Set()
    const cityMap = new Map()

    stateCityPairs.forEach(([state, city]) => {
      statesSet.add(state)
      if (!cityMap.has(state)) {
        cityMap.set(state, new Set())
      }
      cityMap.get(state).add(city)
    })

    const statesList = Array.from(statesSet).map(state => ({
      state,
      stateId: state,
    }))

    const citiesMap = {}
    cityMap.forEach((cities, state) => {
      citiesMap[state] = Array.from(cities).map(city => ({
        city,
        cityId: city,
      }))
    })

    return {
      states: statesList,
      citiesByState: citiesMap,
    }
  }, [stateCityPairs])

  const [stateId, setStateId] = useState([])
  const [cityId, setCityId] = useState([])
  const [stateList, setStateList] = useState(states)
  const [cityList, setCityList] = useState([])
  const [stateNames, setStateNames] = useState(filters.states || [])
  const [cityName, setCityName] = useState(filters.cities || [])
  const [degreeSearch, setDegreeSearch] = useState('')
  const [check, setCheck] = useState(false)
  const [expandedRowKeys, setExpandedRowKeys] = useState([])

  // Combine and process assessment data
  const combinedAssessments = useMemo(() => {
    const activeWithStatus = activeAssessments.map(assessment => ({
      ...assessment,
      status: 'active',
      statusAPI: assessment.status,
      statusIcon: <ArrowDownOutlined style={{ color: '#1890ff' }} />,
      statusText: 'Ongoing'
    }))

    const completedWithStatus = completedAssessments.map(assessment => ({
      ...assessment,
      status: 'completed',
      statusAPI: assessment.status,
      statusIcon: <ArrowUpOutlined style={{ color: '#52c41a' }} />,
      statusText: 'Completed'
    }))

    let combined = [...activeWithStatus, ...completedWithStatus]

    // Apply status filter
    if (statusFilter === 'active') {
      combined = activeWithStatus
    } else if (statusFilter === 'completed') {
      combined = completedWithStatus
    }

    return combined
  }, [activeAssessments, completedAssessments, statusFilter])

  const totalItems = activeTotalItems

  const handleFilterChange = useCallback(
    (key, value) => {
      const newFilters = {
        ...filters,
        ...(typeof key === 'object' ? key : { [key]: value }),
        updateFilters: true,
      }
      onFilterChange(newFilters)
    },
    [filters, onFilterChange]
  )

  useEffect(() => {
    handleFilterChange('type_name', filters.type_name || '')
  }, [])

  useEffect(() => {
    if (!filters.fromSearch && !filters.fromCheckbox) {
      if (filters.type_name !== undefined) {
        setSelectedAssessmentType(filters.type_name)
      }
      if (filters.isTrial !== undefined) {
        setTrialPeriod(filters.isTrial)
      }
      if (filters.isSubscribed !== undefined) {
        setSubscription(filters.isSubscribed)
      }
      if (filters.searchBy !== undefined && !filters.updateFilters) {
        setSearchValue(filters.searchBy || '')
      }
      setStateNames(filters.states || [])
      setCityName(filters.cities || [])
    }
  }, [filters])

  useEffect(() => {
    setStateList(states)
  }, [states])

  useEffect(() => {
    if (stateId?.length) {
      const availableCities = stateId.flatMap(
        state => citiesByState[state] || []
      )
      setCityList(availableCities)
    } else {
      setCityList([])
    }
  }, [stateId, citiesByState])

  useEffect(() => {
    setCityId([])
    setCityName([])
  }, [stateId])

  const handleSearch = useCallback(
    e => {
      const value = e.target.value
      setSearchValue(value)

      handleFilterChange({
        searchBy: value,
        fromSearch: true,
        updateFilters: true,
      })
    },
    [handleFilterChange]
  )

  const handleAssessmentTypeChange = value => {
    setSelectedAssessmentType(value)
  }

  const submitFilter = useCallback(() => {
    setShowFilters(false)

    const newFilters = {
      ...filters,
      type_name: selectedAssessmentType,
      states: stateNames || [],
      cities: cityName || [],
      pageNo: 0,
      updateFilters: true,
    }
    onFilterChange(newFilters)
  }, [filters, selectedAssessmentType, stateNames, cityName, onFilterChange])

  const handleTableChange = useCallback(
    (pagination, tableFilters, sorter) => {
      if (sorter) {
        handleFilterChange({
          sort: sorter.order === 'descend' ? 'desc' : 'asc',
          order: sorter.field,
        })
      }
    },
    [handleFilterChange]
  )

  const handlePageChange = useCallback(
    newPage => {
      onPageChange(newPage, {
        ...filters,
        type_name: selectedAssessmentType,
        pageOnly: true,
      })
    },
    [filters, onPageChange, selectedAssessmentType]
  )

  const AssessmentTypeFilter = () => (
    <div style={{ padding: '10px' }}>
      <Radio.Group
        value={selectedAssessmentType}
        onChange={e => handleAssessmentTypeChange(e.target.value)}
        style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
      >
        {assessmentTypeOptions.map(option => (
          <Radio key={option.value} value={option.value}>
            {option.label}
          </Radio>
        ))}
      </Radio.Group>
    </div>
  )

  const StatusFilter = () => (
    <div style={{ padding: '10px' }}>
      <Radio.Group
        value={statusFilter}
        onChange={e => setStatusFilter(e.target.value)}
        style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
      >
        <Radio value="active">Ongoing</Radio>
        <Radio value="completed">Completed</Radio>
      </Radio.Group>
    </div>
  )

  const menu = useMemo(
    () => (
      <MenuFlex column>
        <StyledTab
          border={true}
          tabPosition={'left'}
          type="card"
          padding="15px"
          popupClassName="filter"
          items={[
            {
              label: 'Assessment Type',
              key: '1',
              children: <AssessmentTypeFilter />,
            },
            {
              label: 'Status',
              key: '2',
              children: <StatusFilter />,
            },
            {
              label: 'State',
              key: '3',
              children: (
                <VirtualizedCheckboxGroup
                  options={states}
                  value={stateId}
                  setValue={setStateId}
                  valueParam="state"
                  labelParam="state"
                  setStateName={setStateNames}
                  filterId={true}
                  stateName={stateNames}
                  degreeSearch={degreeSearch}
                  setDegreeSearch={setDegreeSearch}
                  check={check}
                />
              ),
            },
            {
              label: 'City',
              key: '4',
              children: (
                <>
                  {stateId?.length ? (
                    <VirtualizedCheckboxGroup
                      options={cityList}
                      value={cityId}
                      setValue={setCityId}
                      valueParam="city"
                      labelParam="city"
                      filterId={true}
                      setStateName={setCityName}
                      stateName={cityName}
                      degreeSearch={degreeSearch}
                      setDegreeSearch={setDegreeSearch}
                      check={check}
                    />
                  ) : (
                    <StateCityMessage>
                      Please select state first
                    </StateCityMessage>
                  )}
                </>
              ),
            },
          ]}
        />
        <SubmitFlex right>
          <Button.Secondary
            text="Cancel"
            onClick={() => setShowFilters(false)}
          />
          <Button.Primary
            onClick={submitFilter}
            text="Apply Filter"
            icon={<CheckIconWhite />}
          />
        </SubmitFlex>
      </MenuFlex>
    ),
    [
      states,
      cityList,
      stateId,
      cityId,
      stateNames,
      cityName,
      degreeSearch,
      check,
      selectedAssessmentType,
      statusFilter,
    ]
  )

  const handleExpand = (expanded, record) => {
    if (expanded) {
      setExpandedRowKeys([record.scheduleId])
    } else {
      setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.scheduleId))
    }
  }

  const expandedRowRender = (record) => {
    return <ExpandableContent record={record} onAssessmentClick={onAssessmentClick} />
  }

  // Check if a record has assessmentDetails to determine if it should be expandable
  // One-time assessments are never expandable — they are flat rows
  const rowExpandable = (record) => {
    if (record.isOneTime) return false
    if (record.isStandalone) return false
    return record.assessmentDetails && record.assessmentDetails.length > 0
  }

  const columns = useMemo(
    () => [
      {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <span>STATUS</span>
            {/* <div 
              onClick={() => {
                if (statusFilter === 'completed') {
                  setStatusFilter('active')
                } else {
                  setStatusFilter('completed')
                }
              }}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                padding: '2px 6px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                backgroundColor: '#e6f7ff',
                minWidth: '20px',
                justifyContent: 'center'
              }}
              title={statusFilter === 'active' ? 'Showing ongoing - click to show completed' : 'Showing completed - click to show ongoing'}
            >
              {statusFilter === 'active' ? (
                <ArrowDownOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
              ) : (
                <ArrowUpOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
              )}
            </div> */}
          </div>
        ),
        dataIndex: 'status',
        key: 'status',
        width: '120px',
        fixed: 'left',
        render: (status, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* {record.statusIcon} */}
            <span>{record.statusAPI}</span>
          </div>
        ),
      },
      {
        title: 'ASSESSMENT TYPE / ASSMT. NAME',
        dataIndex: 'assessmentType',
        key: 'assessmentType',
        width: '230px',
        fixed: 'left',
        render: (text, record) => {
          console.log('record', record)
          const typeMap = {
            communication: 'Communication Assessment',
            behavior: 'Behavioral Assessment',
            aptitude: 'Aptitude',
            role_based: 'Role Based Assessment',
            technical_mcq: 'Technical MCQ',
            technical_coding: 'Technical Coding',
          }
          const normalizedType = (text || '').toLowerCase().replace(/[\s-]+/g, '_')
          const isBroadcast = record.isBroadcast || record.mode === 'Broadcast'
          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{typeMap[normalizedType] || typeMap[text] || text || '-'}</span>
                {isBroadcast && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '1px 6px',
                    background: '#EAEDFB', color: '#3249D7', borderRadius: 4,
                  }}>BROADCAST</span>
                )}
              </div>
              <AssessmentName
                onClick={e => {
                  e.stopPropagation()

                  // Helper to get clean schedule name
                  const getCleanScheduleName = (rec) => {
                    const cName = rec.candidateListName;
                    if (cName && cName !== 'N/A') {
                      return cName.replace(/\s*-\s*Student List$/i, '').trim();
                    }
                    return rec.scheduleName;
                  }

                  // One-time assessment: direct click to student table
                  if (record.isOneTime) {
                    onAssessmentClick({
                      ...record,
                      id: record.assessmentInstituteMapId || record.latestAssessmentInstituteMapId,
                      assessmentInstituteMapId: record.assessmentInstituteMapId || record.latestAssessmentInstituteMapId,
                      assessmentType: record.assessmentType,
                      scheduleName: record.scheduleName,
                      isOneTime: true,
                    })
                    return
                  }

                  // Exception: If latestAssessmentInstituteMapId is empty, allow click to go to diagnosis
                  // otherwise, block if Upcoming
                  if (record.status === 'Upcoming' && record.latestAssessmentInstituteMapId) return

                  console.log('🔍 [UnifiedAssessmentTable] Render Debug:', {
                    scheduleName: record.scheduleName,
                    id: record.scheduleId,
                    details: record.assessmentDetails?.length
                  })

                  // If no latest assessment map ID, redirect to diagnosis
                  if (!record.latestAssessmentInstituteMapId) {
                    const diagnosisData = {
                      ...record,
                      scheduleId: record.scheduleId,
                      isDiagnosis: true,
                      assessmentType: record.assessmentType,
                      scheduleName: getCleanScheduleName(record),
                    }
                    onAssessmentClick(diagnosisData)
                    return
                  }

                  const detail = record.assessmentDetails?.find(
                    d => d.assessmentInstituteMapId === record.latestAssessmentInstituteMapId
                  )

                  // Calculate schedule number
                  let scheduleNum = 1
                  if (detail && record.assessmentDetails) {
                    const idx = record.assessmentDetails.findIndex(d => d.assessmentInstituteMapId === detail.assessmentInstituteMapId)
                    if (idx !== -1) {
                      scheduleNum = idx + 1
                    }
                  }

                  const assessmentToPass = detail ? {
                    ...record,
                    ...detail,
                    id: detail.assessmentInstituteMapId,
                    assessmentInstituteMapId: detail.assessmentInstituteMapId,
                    scheduleName: getCleanScheduleName(record),
                    scheduleNumber: scheduleNum,
                  } : {
                    ...record,
                    scheduleName: getCleanScheduleName(record),
                    scheduleNumber: 1
                  }

                  onAssessmentClick(assessmentToPass)
                }}
                role="button"
                tabIndex={0}
                style={{
                  // Cursor is pointer if not upcoming OR if it is upcoming but has no map ID (diagnosis fallback)
                  cursor: (record.status !== 'Upcoming' || !record.latestAssessmentInstituteMapId) ? 'pointer' : 'not-allowed',
                  opacity: (record.status !== 'Upcoming' || !record.latestAssessmentInstituteMapId) ? 1 : 0.5
                }}
                onKeyPress={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()

                    // One-time assessment: direct click
                    if (record.isOneTime) {
                      onAssessmentClick({
                        ...record,
                        id: record.assessmentInstituteMapId || record.latestAssessmentInstituteMapId,
                        assessmentInstituteMapId: record.assessmentInstituteMapId || record.latestAssessmentInstituteMapId,
                        assessmentType: record.assessmentType,
                        scheduleName: record.scheduleName,
                        isOneTime: true,
                      })
                      return
                    }

                    if (record.status === 'Upcoming' && record.latestAssessmentInstituteMapId) return

                    const getCleanScheduleName = (rec) => {
                      const cName = rec.candidateListName;
                      if (cName && cName !== 'N/A') {
                        return cName.replace(/\s*-\s*Student List$/i, '').trim();
                      }
                      return rec.scheduleName;
                    }

                    if (!record.latestAssessmentInstituteMapId) {
                      const diagnosisData = {
                        ...record,
                        scheduleId: record.scheduleId,
                        isDiagnosis: true,
                        assessmentType: record.assessmentType,
                        scheduleName: getCleanScheduleName(record),
                      }
                      onAssessmentClick(diagnosisData)
                      return
                    }

                    const detail = record.assessmentDetails?.find(
                      d => d.assessmentInstituteMapId === record.latestAssessmentInstituteMapId
                    )

                    let scheduleNum = 1
                    if (detail && record.assessmentDetails) {
                      const idx = record.assessmentDetails.findIndex(d => d.assessmentInstituteMapId === detail.assessmentInstituteMapId)
                      if (idx !== -1) {
                        scheduleNum = idx + 1
                      }
                    }

                    const assessmentToPass = detail ? {
                      ...record,
                      ...detail,
                      id: detail.assessmentInstituteMapId,
                      assessmentInstituteMapId: detail.assessmentInstituteMapId,
                      scheduleName: getCleanScheduleName(record),
                      scheduleNumber: scheduleNum,
                    } : {
                      ...record,
                      scheduleName: getCleanScheduleName(record),
                      scheduleNumber: 1
                    }

                    onAssessmentClick(assessmentToPass)
                  }
                }}
              >
                {record.scheduleName}
              </AssessmentName>

            </div>
          )
        },
      },
      {
        title: 'ASSMT. SENT / TAKEN',
        dataIndex: 'assessmentSent',
        key: 'assessmentProgress',
        width: 280,
        render: (sent, record) => {
          const completed = record.assessmentsTaken || 0
          const total = sent || record.assessmentsSent || 0

          if (record.status === 'completed') {
            return (
              <CompletedAssessmentProgressBar
                sent={total}
                completed={completed}
              />
            )
          } else {
            return (
              <AssessmentProgressBar
                sent={total}
                completed={completed}
              />
            )
          }
        },
      },
      {
        title: 'Expired Assmts.',
        dataIndex: 'candidates',
        key: 'candidates',
        width: '120px',
        render: (candidates, record) => {
          // For active assessments, use assessmentSent
          // For completed assessments, use assessmentTotal
          const candidateCount = record.expiredAssessments || 0
          return (
            <div style={{ fontWeight: '500', color: '#000000' }}>
              {candidateCount}
            </div>
          )
        },
      },
      // {
      //   title: 'Practice Tests',
      //   dataIndex: 'candidates',
      //   key: 'candidates',
      //   width: '120px',
      //   render: (candidates, record) => {
      //     // For active assessments, use assessmentSent
      //     // For completed assessments, use assessmentTotal
      //     return (
      //       <div style={{ fontWeight: '500', color: '#000000' }}>
      //         -1
      //       </div>
      //     )
      //   },
      // }, 

      {
        title: 'CANDIDATES',
        dataIndex: 'candidates',
        key: 'candidates',
        width: '140px',
        render: (candidates, record) => {
          // For active assessments, use assessmentSent
          // For completed assessments, use assessmentTotal
          const candidateCount = record.totalCandidates || 0
          return (
            <div style={{ fontWeight: '500', color: '#000000' }}>
              {candidateCount}
            </div>
          )
        },
      },

      // {
      //   title: 'Candidate list Name',
      //   dataIndex: 'degreeDepartment',
      //   key: 'degreeDepartment',
      //   width: 180,
      //   render: (degrees, record) => (
      //     <div>
      //       {record.candidateListName}
      //     </div>
      //   ),
      // },
      {
        title: 'Assmt. Validity',
        dataIndex: 'candidates',
        key: 'candidates',
        width: '140px',
        render: (candidates, record) => {
          // For active assessments, use assessmentSent
          // For completed assessments, use assessmentTotal
          return (
            <div >
              {record.assessmentValidity}
            </div>
          )
        },
      },
      {
        title: 'Schedule Start Date & End Date',
        dataIndex: 'assessmentSubDate',
        key: 'assessmentSubDate',
        width: '200px',
        sorter: true,
        render: (startDate, record) => {
          if (record.status === 'completed') {
            return <div>{formatDate(record.completedDate)}
              <Button.Secondary
                text="View on Calendar"
                onClick={() => {
                  setShowViewCalendar(true)
                  setViewCalendarData(record)
                }}
              />
            </div>
          } else {
            return (
              <div>
                {formatDate(record.startDate)}{' '}
                {record.endDate ? ` - ${formatDate(record.endDate)}` : ''}
                <Button.Secondary
                  text="View on Calendar"
                  onClick={() => {
                    setShowViewCalendar(true)
                    setViewCalendarData(record)
                  }}
                />
              </div>
            )
          }
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 80,
        align: 'center',
        render: (_, record) => (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
            {record.scheduleId && !record.isOneTime && !record.isStandalone && (
              <Tooltip title="Export">
                <AntButton
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  style={{ color: '#595959', borderRadius: 6, width: 32, height: 32 }}
                  onClick={async (e) => {
                    e.stopPropagation()
                    try {
                      message.loading({ content: 'Exporting schedule...', key: 'scheduleExport' })
                      await dispatch(exportScheduleToExcel(record.scheduleId, instituteId, passingYear))
                      message.success({ content: 'Schedule exported!', key: 'scheduleExport' })
                    } catch (error) {
                      message.error({ content: 'Export failed', key: 'scheduleExport' })
                    }
                  }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [onAssessmentClick, statusFilter, setStatusFilter, dispatch, instituteId, passingYear]
  )

  // Calculate dynamic values based on status filter
  const currentTotalItems =
    statusFilter === 'active' ? activeTotalItems : completedTotalItems
  const currentLimit =
    statusFilter === 'active' ? activeLimit : completedLimit
  const currentPage =
    statusFilter === 'active' ? activePage : completedPage

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >



      </div>
      <TableTop>
        <SearchSection>
          <Input
            placeholder="Search by keyword"
            prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
            value={searchValue}
            onChange={handleSearch}
          />
        </SearchSection>

        {/* <FilterContainer>
          <FilterButtonComponent onClick={() => setShowFilters(!showFilters)}>
            <FilterIcon />
            <span>Filter</span>
            <ArrowDropDown />
          </FilterButtonComponent>
          {showFilters && (
            <>
              <FilterPopup>{menu}</FilterPopup>
              <Overlay onClick={() => setShowFilters(false)} />
            </>
          )}
        </FilterContainer> */}

        <ShowingText>
          Showing {Math.min(combinedAssessments.length)} of{' '}
          {currentTotalItems}
        </ShowingText>
      </TableTop>

      <Spin spinning={loading}>

        <CustomStyledTable
          columns={columns}
          dataSource={combinedAssessments}
          pagination={false}
          rowKey="scheduleId"
          scroll={{ x: '1280px' }}
          onChange={handleTableChange}
          expandable={{
            expandedRowRender,
            expandedRowKeys: expandedRowKeys,
            onExpand: handleExpand,
            rowExpandable: rowExpandable,
            expandIconColumnIndex: 0,
            expandIcon: ({ expanded, onExpand, record }) => (
              rowExpandable(record) ? (
                <span
                  onClick={e => {
                    e.stopPropagation()
                    onExpand(record, e)
                  }}
                  style={{
                    cursor: 'pointer',
                    marginRight: 8,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 16,
                    height: 16
                  }}
                >
                  {expanded ? <DownSideArrowIco /> : <RightSideArrowIco />}
                </span>
              ) : null
            ),
          }}
        />
      </Spin>

      {currentTotalItems > currentLimit && (
        <div
          style={{
            margin: '16px',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Pagination
            current={currentPage}
            total={currentTotalItems}
            pageSize={currentLimit}
            onChange={handlePageChange}
          />
        </div>
      )}

      <CustomDateCalendarDrawer
        visible={showViewCalendar}
        onClose={() => { setShowViewCalendar(false), setViewCalendarData(null) }}
        startDate={viewCalendarData?.startDate}
        endDate={viewCalendarData?.endDate}
        selectedDates={
          viewCalendarData?.scheduledDates ||
          (viewCalendarData?.assessmentDetails
            ? viewCalendarData.assessmentDetails
              .map(detail => moment.utc(detail.startTime).format('YYYY-MM-DD'))
              .filter((date, index, self) => self.indexOf(date) === index)
            : [])
        }
        onDatesChange={() => {
          // This is view-only, so no changes allowed
        }}
        minDates={0}
        isViewOnly={true}
        title="Calendar View"
        frequencyType={viewCalendarData?.frequencyType}
        scheduledDates={viewCalendarData?.scheduledDates || []}
        isEmpty={viewCalendarData?.isEmpty}
      />
    </>
  )
}

export default UnifiedAssessmentTable
